import { isPermanentArchiveSizeError } from '../../trigger/lib/download-archive-budget';
import { normalizeLocale } from '@/i18n/config';
import type { DownloadManagerDockCorner } from '@/lib/download-manager-docking';
import type { ManagedDownloadDeliveryMode } from '@/lib/managed-download-delivery';
import { EMPTY_FOLDER_DOWNLOAD_ERROR } from '../../trigger/download-job-contract';

export type DownloadManagerJobStatus =
  | 'queued'
  | 'running'
  | 'ready'
  | 'failed'
  | 'expired'
  | 'cancelled';

export type DownloadManagerJob = {
  id: string;
  archiveName: string;
  kind: 'asset_pack' | 'folder_zip' | 'direct_asset';
  deliveryMode: ManagedDownloadDeliveryMode;
  status: DownloadManagerJobStatus;
  progress: number;
  itemCount: number;
  completedItemCount: number;
  estimatedSourceBytes?: number;
  unknownSizeItemCount?: number;
  outputSizeBytes?: number;
  createdAt: number;
  startedAt?: number;
  deliveredAt?: number;
  errorMessage?: string;
  /** A localized reason, for a download that failed in this browser. */
  reasonText?: string;
  /** False when another try cannot help (the file is unavailable). */
  retryable?: boolean;
};

export type DownloadManagerPresentationState = {
  isOpen: boolean;
  isExpanded: boolean;
  autoDeliveryJobIds: string[];
  dockCorner: DownloadManagerDockCorner;
};

export type DownloadManagerPresentationAction =
  | { type: 'close' }
  | { type: 'dock'; corner: DownloadManagerDockCorner }
  | { type: 'expand' }
  | { type: 'minimize' }
  | { type: 'consume-auto-delivery'; jobId: string }
  | { type: 'register-auto-delivery'; jobId: string };

export function createDownloadManagerPresentationState(): DownloadManagerPresentationState {
  return {
    isOpen: true,
    isExpanded: true,
    autoDeliveryJobIds: [],
    dockCorner: 'bottom-right',
  };
}

export function reduceDownloadManagerPresentation(
  state: DownloadManagerPresentationState,
  action: DownloadManagerPresentationAction
): DownloadManagerPresentationState {
  switch (action.type) {
    case 'close':
      return { ...state, isOpen: false };
    case 'dock':
      return { ...state, dockCorner: action.corner };
    case 'expand':
      return { ...state, isOpen: true, isExpanded: true };
    case 'minimize':
      return { ...state, isExpanded: false };
    case 'consume-auto-delivery':
      return {
        ...state,
        autoDeliveryJobIds: state.autoDeliveryJobIds.filter(
          (jobId) => jobId !== action.jobId
        ),
      };
    case 'register-auto-delivery': {
      const hasIntent = state.autoDeliveryJobIds.includes(action.jobId);
      return {
        isOpen: true,
        isExpanded: true,
        autoDeliveryJobIds: hasIntent
          ? state.autoDeliveryJobIds
          : [...state.autoDeliveryJobIds, action.jobId],
        dockCorner: state.dockCorner,
      };
    }
  }
}

export function isOrgSettingsPath(pathname: string, orgSlug: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === orgSlug && segments[1] === 'settings';
}

export function shouldRenderDownloadManager({
  hasResolvedDockCorner,
  isOpen,
  jobCount,
  pathname,
  orgSlug,
}: {
  hasResolvedDockCorner: boolean;
  isOpen: boolean;
  jobCount: number;
  pathname: string;
  orgSlug: string;
}): boolean {
  return (
    hasResolvedDockCorner &&
    isOpen &&
    jobCount > 0 &&
    !isOrgSettingsPath(pathname, orgSlug)
  );
}

export function canCloseDownloadManager(
  jobs: DownloadManagerJob[],
  isDelivering: boolean
): boolean {
  if (isDelivering) return false;
  return jobs.every(
    (job) => job.status !== 'queued' && job.status !== 'running'
  );
}

export function getDownloadManagerCollapsedJob(
  jobs: DownloadManagerJob[]
): DownloadManagerJob | null {
  const activeJobs = jobs.filter(
    (job) => job.status === 'queued' || job.status === 'running'
  );
  const candidates = activeJobs.length > 0 ? activeJobs : jobs;

  return candidates.reduce<DownloadManagerJob | null>((latest, job) => {
    if (!latest || job.createdAt > latest.createdAt) return job;
    return latest;
  }, null);
}

export type DownloadManagerFileDisplay =
  | { kind: 'progress'; completed: number; total: number }
  | { kind: 'count'; count: number };

export type DownloadManagerRowAction = 'download' | 'retry';

export function getDownloadManagerRowAction(
  job: DownloadManagerJob
): DownloadManagerRowAction | null {
  if (job.status === 'ready') return 'download';
  if (
    job.status === 'failed' &&
    job.retryable !== false &&
    !isPermanentArchiveSizeError(job.errorMessage)
  )
    return 'retry';
  return null;
}

export function isEmptyFolderDownloadJob(job: DownloadManagerJob): boolean {
  return (
    job.kind === 'folder_zip' &&
    job.status === 'failed' &&
    job.errorMessage === EMPTY_FOLDER_DOWNLOAD_ERROR
  );
}

export function getDownloadManagerFileDisplay(
  job: DownloadManagerJob
): DownloadManagerFileDisplay | null {
  const total = Math.max(0, Math.floor(job.itemCount));
  if (total === 0) return null;

  const completed = Math.min(
    total,
    Math.max(0, Math.floor(job.completedItemCount))
  );
  if (job.status === 'queued' || job.status === 'running') {
    return { kind: 'progress', completed, total };
  }
  if (job.status === 'ready') {
    return { kind: 'count', count: completed };
  }
  return { kind: 'count', count: total };
}

export function getDownloadManagerStartedAt(job: DownloadManagerJob): number {
  return job.startedAt ?? job.createdAt;
}

export function formatDownloadManagerTime(
  timestamp: number,
  locale?: string | null
): string {
  const normalizedLocale = normalizeLocale(locale);
  return new Intl.DateTimeFormat(normalizedLocale, {
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(timestamp)
    .toLocaleLowerCase(normalizedLocale);
}
